import { useState } from 'react'

import { ActionIcon, Collapse, Text, Tooltip } from '@mantine/core'
import { ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react'
import type { EmploymentVerificationAction } from 'types/graphql'

import { VoEActionStatus } from 'src/graphql/types/verificationOfEmployments'
import { formatDateCustom, formatSnakeValue } from 'src/lib/formatters'

import {
  attemptNumber,
  OUTREACH_STATUS_COLOR,
  typeLabel,
} from './outreachActionLabels'

const TABLE_DATE_FORMAT = 'MM/DD h:mm A'

interface ScheduledActionsTableProps {
  actions: EmploymentVerificationAction[]
  onReschedule: (action: EmploymentVerificationAction) => void
  onCancel: (actionId: string) => void
  cancellingActionId: string | null
}

/**
 * Every outreach action on the VoE — sent, upcoming and cancelled — so a
 * recruiter can see the whole email and call sequence, not just the next
 * attempt the audit trail shows.
 */
const ScheduledActionsTable = ({
  actions,
  onReschedule,
  onCancel,
  cancellingActionId,
}: ScheduledActionsTableProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  return (
    <div className="mt-3 pb-4">
      <button
        className="mb-2 flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
        onClick={() => setIsExpanded((v) => !v)}
      >
        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Text size="xs" fw={400} className="text-inherit">
          SCHEDULED ACTIONS
          <span className="ml-1 text-doubleNickel-gray-400">
            ({actions.length})
          </span>
        </Text>
      </button>
      <Collapse in={isExpanded}>
        {actions.length === 0 ? (
          <Text
            size="xs"
            className="py-1 text-center text-doubleNickel-gray-400"
          >
            No outreach actions
          </Text>
        ) : (
          <div className="rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-1">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="text-left text-doubleNickel-gray-500">
                  <th className="border-b border-doubleNickel-gray-200 px-1.5 py-1 font-medium">
                    #
                  </th>
                  <th className="border-b border-doubleNickel-gray-200 px-1.5 py-1 font-medium">
                    Type
                  </th>
                  <th className="border-b border-doubleNickel-gray-200 px-1.5 py-1 font-medium">
                    Status
                  </th>
                  <th className="border-b border-doubleNickel-gray-200 px-1.5 py-1 font-medium">
                    Scheduled
                  </th>
                  <th className="border-b border-doubleNickel-gray-200 px-1.5 py-1 font-medium">
                    Ran
                  </th>
                  <th className="border-b border-doubleNickel-gray-200" />
                </tr>
              </thead>
              <tbody>
                {actions.map((action) => (
                  <tr
                    key={action.voeActionId}
                    className="text-doubleNickel-gray-700 [&:not(:last-child)>td]:border-b [&:not(:last-child)>td]:border-doubleNickel-gray-100"
                  >
                    <td className="whitespace-nowrap px-1.5 py-1">
                      {attemptNumber(action) ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-1">
                      {typeLabel(action.type)}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-1">
                      <span
                        aria-hidden="true"
                        className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${
                          OUTREACH_STATUS_COLOR[
                            action.status as VoEActionStatus
                          ] ?? 'bg-doubleNickel-gray-400'
                        }`}
                      />
                      {formatSnakeValue(action.status)}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-1">
                      {formatDateCustom(action.scheduledAt, TABLE_DATE_FORMAT)}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-1">
                      {action.executedAt ? (
                        formatDateCustom(action.executedAt, TABLE_DATE_FORMAT)
                      ) : (
                        <span className="text-doubleNickel-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-0.5 py-0 text-right">
                      {action.status === VoEActionStatus.SCHEDULED && (
                        <div className="flex items-center justify-end gap-0.5">
                          <Tooltip label="Reschedule">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="xs"
                              aria-label="Reschedule"
                              onClick={() => onReschedule(action)}
                            >
                              <RefreshCw
                                size={11}
                                className="stroke-doubleNickel-gray-500"
                              />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Cancel">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="xs"
                              aria-label="Cancel"
                              loading={
                                cancellingActionId === action.voeActionId
                              }
                              onClick={() => onCancel(action.voeActionId)}
                            >
                              <X
                                size={11}
                                className="stroke-doubleNickel-gray-500"
                              />
                            </ActionIcon>
                          </Tooltip>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Collapse>
    </div>
  )
}

export default ScheduledActionsTable
